/**
 * المحلل الذكي للملف الطبي — the AI diagnostic console.
 *
 * Opens over the Live View while a patient is in the room, reads their entire
 * record server-side, and streams back ten lines: what is abnormal, and why it
 * might be.
 *
 * The design brief was "this must not look like a secondary tool", and the
 * thing that actually delivers that is not the glow — it is that nothing here
 * ever shows a bare spinner. From the first frame the panel is reporting: what
 * it is reading (record counts), which model it is on, when a model fails and
 * it drops to the next, what the model is thinking, and then the findings, one
 * at a time, on coloured severity rails.
 *
 * Deliberately NOT the shared `<Modal>`: that component's chrome is a flat blue
 * header bar, and the console needs its own shell — an aurora rim, an ambient
 * grid, and a header that carries live telemetry. It reuses the same overlay
 * conventions (backdrop click, Escape, scroll lock) so it behaves like every
 * other dialog in the app even though it does not look like one.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAiCapabilities, useMedicalFileAnalysis } from './useAiAnalysis';
import { stripInline } from './aiStream';
import { buildReportDocument, shortModel, TONE_LABEL } from './reportDocument';

/* ==========================================================================
   Helpers
   ========================================================================== */

const TONE_ICON = {
  critical: 'alert-triangle',
  warning: 'alert-circle',
  minor: 'info',
  gap: 'help-circle',
  action: 'check-circle-2',
  summary: 'stethoscope',
  notice: 'alert-triangle',
  neutral: 'circle',
};

const TONE_TEXT = {
  critical: 'text-red-300',
  warning: 'text-orange-300',
  minor: 'text-yellow-300',
  gap: 'text-sky-300',
  action: 'text-emerald-300',
  summary: 'text-purple-200',
  notice: 'text-amber-300',
  neutral: 'text-slate-300',
};

/* ==========================================================================
   The console
   ========================================================================== */

export function MedicalAiAnalyzer({
  open,
  onClose,
  patientId,
  manualPatientCode,
  patientName,
  specialty,
}) {
  const { success, error: toastError } = useNotifications();
  const capabilities = useAiCapabilities({ enabled: open });
  const analysis = useMedicalFileAnalysis();

  const [premium, setPremium] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);
  const reportRef = useRef(null);
  const reasoningRef = useRef(null);
  const startedOnce = useRef(false);

  /* --- lifecycle --------------------------------------------------------- */

  // Escape closes, exactly as the shared Modal does. A dialog that traps the
  // key everyone reaches for first is worse than one that looks plainer.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const startAnalysis = useCallback(
    (usePremium) => {
      analysis.run({
        patientId,
        manualPatientCode,
        premium: usePremium,
        specialty,
      });
    },
    // `analysis.run` is stable (useCallback with no deps); listing the whole
    // object would re-create this on every streamed token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientId, manualPatientCode, specialty],
  );

  // Start as soon as the console opens. The doctor already expressed intent by
  // pressing the button; making them press a second one inside the modal is a
  // click that carries no decision.
  useEffect(() => {
    if (open && !startedOnce.current) {
      startedOnce.current = true;
      startAnalysis(false);
    }
    if (!open) {
      startedOnce.current = false;
      analysis.reset();
      setPremium(false);
      setShowReasoning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* --- autoscroll -------------------------------------------------------- */

  // Both panes follow their own tail while streaming. Scoped to the pane rather
  // than the page, so a doctor reading the top of a finished report is not
  // yanked to the bottom by the reasoning drawer still filling.
  useEffect(() => {
    if (analysis.isStreaming && reportRef.current) {
      reportRef.current.scrollTop = reportRef.current.scrollHeight;
    }
  }, [analysis.report, analysis.isStreaming]);

  useEffect(() => {
    if (showReasoning && reasoningRef.current) {
      reasoningRef.current.scrollTop = reasoningRef.current.scrollHeight;
    }
  }, [analysis.reasoning, showReasoning]);

  /* --- actions ----------------------------------------------------------- */

  const togglePremium = () => {
    const next = !premium;
    setPremium(next);
    startAnalysis(next);
  };

  /**
   * Copy the report — the findings, not the raw stream.
   *
   * `analysis.report` is everything the model emitted, and on a reasoning model
   * that includes its entire chain of thought. Pasting that into a patient note
   * is the same bug the printer had.
   */
  const copyReport = async () => {
    const text = analysis.findings
      .map((finding) => `${finding.marker || '•'} ${stripInline(finding.text)}`)
      .join('\n');

    if (!text) {
      toastError('لا يوجد تقرير صالح للنسخ');
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      success('تم نسخ التقرير');
    } catch {
      toastError('تعذر النسخ — المتصفح منع الوصول للحافظة');
    }
  };

  /**
   * Print the report as an A4 clinical document.
   *
   * A new window rather than a print stylesheet on this one: the console is a
   * modal over a live queue, and `window.print()` here would try to render the
   * aurora rim, the queue behind it and a scan line onto A4.
   *
   * The document itself is built in reportDocument.js — it is the one artefact
   * of this feature that leaves the screen, so it lives somewhere it can be
   * previewed and tested rather than inside a modal's closure.
   */
  const printReport = () => {
    // Never print the raw stream. A reasoning model emits its chain of thought
    // as ordinary content, and printing that produced a five-page PDF of the
    // model talking to itself. `findings` has already been filtered by the
    // severity contract.
    if (!analysis.followedFormat || analysis.findings.length === 0) {
      toastError('لا يوجد تقرير صالح للطباعة — أعد التحليل أولاً');
      return;
    }

    const sheet = window.open('', '_blank', 'width=900,height=1000');
    if (!sheet) {
      toastError('المتصفح منع فتح نافذة الطباعة');
      return;
    }

    sheet.document.write(
      buildReportDocument({
        findings: analysis.findings,
        patientName,
        patientCode: manualPatientCode,
        meta: analysis.meta,
        tier: servedTier,
      }),
    );

    sheet.document.close();
    sheet.focus();
    // Chrome needs a tick to lay the document out; printing immediately after
    // document.close() can open the dialog against a half-rendered sheet.
    sheet.setTimeout(() => sheet.print(), 250);
  };

  /* --- derived ----------------------------------------------------------- */

  const consoleState = useMemo(() => {
    if (analysis.status === 'error') return 'error';
    if (analysis.isStreaming) return 'streaming';
    if (analysis.status === 'done') return 'done';
    return 'idle';
  }, [analysis.status, analysis.isStreaming]);

  const servedTier = analysis.meta.tier || (premium ? 'premium' : 'standard');
  const counts = analysis.meta.counts;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-black/85 p-3 backdrop-blur-md sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="تحليل الملف الطبي بالذكاء الاصطناعي"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="ai-console flex max-h-[94vh] w-full max-w-3xl flex-col"
        data-state={consoleState}
        data-tier={servedTier}
      >
        <div className="ai-grid" aria-hidden="true" />

        {/* ── Header ───────────────────────────────────────────────────── */}
        <header className="relative z-10 flex shrink-0 items-start gap-4 border-b border-white/10 p-4 sm:p-5">
          <Orb streaming={analysis.isStreaming} tier={servedTier} />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-black text-white sm:text-xl">التحليل الذكي للملف الطبي</h2>
              <TierBadge tier={servedTier} />
            </div>

            <p className="mt-1 truncate text-sm text-slate-400">
              {patientName || 'المريض الحالي'}
              {counts && (
                <span className="text-slate-500">
                  {' '}
                  · {counts.prescriptions} روشتة · {counts.externalRecords} مستند خارجي ·{' '}
                  {counts.bookings} زيارة
                </span>
              )}
            </p>

            <StatusLine analysis={analysis} />
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="إغلاق"
            className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Icon name="x" size={20} />
          </button>
        </header>

        {/* ── Controls ─────────────────────────────────────────────────── */}
        <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2 border-b border-white/[0.07] px-4 py-3 sm:px-5">
          <PremiumToggle
            on={premium}
            capabilities={capabilities}
            disabled={analysis.isStreaming}
            onToggle={togglePremium}
          />

          <div className="flex-1" />

          {analysis.isStreaming ? (
            <ToolButton icon="square" label="إيقاف" onClick={analysis.stop} tone="danger" />
          ) : (
            <ToolButton
              icon="refresh-cw"
              label="إعادة التحليل"
              onClick={() => startAnalysis(premium)}
            />
          )}

          <ToolButton
            icon="copy"
            label="نسخ"
            onClick={copyReport}
            disabled={analysis.findings.length === 0 || analysis.isStreaming || !analysis.followedFormat}
          />
          <ToolButton
            icon="printer"
            label="طباعة"
            onClick={printReport}
            disabled={analysis.findings.length === 0 || analysis.isStreaming || !analysis.followedFormat}
          />
        </div>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="custom-scrollbar relative z-10 flex-1 overflow-y-auto" ref={reportRef}>
          {analysis.isStreaming && <div className="ai-scan" aria-hidden="true" />}

          <div className="relative space-y-3 p-4 sm:p-5">
            <FallbackLadder ladder={analysis.ladder} />

            <ReasoningDrawer
              // Two sources, one drawer: tokens the model sent on the reasoning
              // channel, and lines it spoke aloud as ordinary content that the
              // report contract rejected. Both are thinking; neither is a finding.
              text={[analysis.reasoning, analysis.aside.join('\n')]
                .filter(Boolean)
                .join('\n')}
              open={showReasoning}
              streaming={analysis.isStreaming}
              onToggle={() => setShowReasoning((v) => !v)}
              scrollRef={reasoningRef}
            />

            {analysis.meta.downgraded && analysis.meta.downgradeMessage && (
              <Notice tone="amber" icon="alert-triangle" text={analysis.meta.downgradeMessage} />
            )}

            {analysis.meta.truncated &&
              Object.values(analysis.meta.truncated).some(Boolean) && (
                <Notice
                  tone="sky"
                  icon="info"
                  text="الملف كبير — تم تحليل أحدث السجلات فقط. الأقدم منها لم يدخل في هذا التقرير."
                />
              )}

            {analysis.error && <Notice tone="red" icon="alert-triangle" text={analysis.error} />}

            <Findings analysis={analysis} />
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <footer className="relative z-10 shrink-0 border-t border-white/10 bg-black/25 px-4 py-3 sm:px-5">
          <p className="flex items-start gap-2 text-[11px] leading-relaxed text-slate-500">
            <Icon name="shield-check" size={13} className="mt-px shrink-0 text-slate-600" />
            {/* Not boilerplate. An AI opinion presented next to real prescriptions
                on a doctor's screen needs its status stated where it is read,
                not buried in a terms page. */}
            <span>
              أداة دعم قرار — يقرأ النموذج الملف المسجّل على المنصة فقط، ولا يُغني عن الفحص
              الإكلينيكي ولا يُعد تشخيصاً. القرار الطبي والمسؤولية على الطبيب المعالج.
            </span>
          </p>
        </footer>
      </div>
    </div>
  );
}

/* ==========================================================================
   Pieces
   ========================================================================== */

function Orb({ streaming, tier }) {
  return (
    <div className="ai-orb shrink-0" data-tier={tier} aria-hidden="true">
      {streaming && (
        <>
          <span className="ai-orb__ring" />
          <span className="ai-orb__ring" />
          <span className="ai-orb__ring" />
        </>
      )}
      <span className="ai-orb__core" />
    </div>
  );
}

function TierBadge({ tier }) {
  const premium = tier === 'premium';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[10px] font-black ${
        premium
          ? 'border-amber-400/40 bg-amber-400/15 text-amber-200'
          : 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200'
      }`}
    >
      <Icon name={premium ? 'crown' : 'zap'} size={11} />
      {premium ? 'النموذج المتقدم' : 'النموذج القياسي'}
    </span>
  );
}

/**
 * The one line that says what is happening now.
 *
 * Four distinct states, never a generic "loading". "جاري القراءة" and "النموذج
 * يفكر" are different facts, and a doctor watching a slow response deserves to
 * know which one they are waiting on.
 */
function StatusLine({ analysis }) {
  const { status, isStreaming, meta, timing, report, reasoning, findings } = analysis;

  if (status === 'idle') {
    return <p className="mt-1.5 text-xs text-slate-500">جاهز للتحليل…</p>;
  }

  if (isStreaming) {
    const phase = report
      ? 'يكتب التقرير'
      : reasoning
        ? 'النموذج يفكر'
        : meta.stage === 'prepared'
          ? 'يقرأ الملف الطبي'
          : 'يتصل بمحرك التحليل';

    return (
      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-cyan-300">
        <Icon name="loader-2" size={12} className="animate-spin" />
        <span className="font-bold">{phase}…</span>
        {meta.model && (
          <span className="text-slate-500" dir="ltr">
            {shortModel(meta.model)}
          </span>
        )}
        {timing.firstTokenMs != null && (
          <span className="text-slate-600">أول رد خلال {timing.firstTokenMs} ملي/ث</span>
        )}
      </p>
    );
  }

  if (status === 'error') {
    return <p className="mt-1.5 text-xs font-bold text-red-300">تعذر إكمال التحليل</p>;
  }

  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
      <Icon name="check-circle-2" size={12} className="text-emerald-400" />
      <span className="text-emerald-300">
        {analysis.stopped ? 'تم الإيقاف' : 'اكتمل التحليل'}
      </span>
      <span>{findings.length} ملاحظة</span>
      {timing.totalMs != null && <span>خلال {(timing.totalMs / 1000).toFixed(1)} ثانية</span>}
      {meta.model && (
        <span dir="ltr" className="text-slate-600">
          {shortModel(meta.model)}
        </span>
      )}
    </p>
  );
}

/**
 * The premium toggle, and every reason it might not be usable.
 *
 * A disabled switch with no explanation is the most frustrating control in any
 * product. Each blocked state says which one it is, and the doctor knows
 * whether to ask the admin, wait until tomorrow, or do nothing.
 */
function PremiumToggle({ on, capabilities, disabled, onToggle }) {
  const { canUsePremium, loading, usage, premiumDailyLimit, premiumModelLabel } = capabilities;

  if (loading) {
    return (
      <span className="flex items-center gap-2 text-xs text-slate-500">
        <Icon name="loader-2" size={13} className="animate-spin" />
        جاري التحقق من الصلاحيات…
      </span>
    );
  }

  // Not granted: the toggle is absent entirely, not greyed out. A doctor who
  // was never given a paid capability should not spend attention on a control
  // they cannot use — and its absence is the RBAC rule, made visible.
  if (!canUsePremium) {
    return (
      <span className="flex items-center gap-1.5 text-[11px] text-slate-600">
        <Icon name="lock" size={12} />
        النموذج المتقدم غير مُفعّل لحسابك
      </span>
    );
  }

  const quotaSpent = premiumDailyLimit > 0 && usage.premium >= premiumDailyLimit;

  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        className="ai-toggle"
        data-on={String(on)}
        role="switch"
        aria-checked={on}
        aria-label="تشغيل النموذج المتقدم"
        disabled={disabled || quotaSpent}
        onClick={onToggle}
      >
        <span className="ai-toggle__knob" />
      </button>

      <div className="leading-tight">
        <div className="flex items-center gap-1.5 text-xs font-bold text-amber-200">
          <Icon name="crown" size={12} />
          تحليل أعمق بالنموذج المتقدم
        </div>
        <div className="text-[10px] text-slate-500">
          {quotaSpent ? (
            <span className="text-amber-400/80">
              انتهت حصة اليوم ({premiumDailyLimit}) — تتجدد غداً
            </span>
          ) : (
            <>
              {premiumModelLabel && (
                <span dir="ltr" className="ltr:mr-1 rtl:ml-1">
                  {shortModel(premiumModelLabel)}
                </span>
              )}
              {premiumDailyLimit > 0
                ? `· استُخدم ${usage.premium} من ${premiumDailyLimit} اليوم`
                : '· بلا حد يومي'}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The fallback ladder, live.
 *
 * The reference implementation's fallback loop was invisible — it worked, and
 * nobody could tell. Surfacing it is what turns a slow first response from
 * "this is broken" into "the first model is busy, it is trying another", which
 * is both true and reassuring.
 *
 * Hidden entirely when the first rung succeeds: a one-row list explaining that
 * nothing went wrong is noise.
 */
function FallbackLadder({ ladder }) {
  if (ladder.length <= 1) return null;

  return (
    <div className="rounded-xl border border-white/[0.07] bg-black/25 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-black text-slate-500">
        <Icon name="network" size={12} />
        سلسلة النماذج البديلة
      </div>

      <ol className="space-y-1">
        {ladder.map((rung, index) => (
          <li
            key={`${rung.model}-${index}`}
            className="ai-rung flex items-center gap-2 text-[11px]"
            data-state={rung.state}
          >
            <Icon
              name={
                rung.state === 'served'
                  ? 'check-circle-2'
                  : rung.state === 'failed'
                    ? 'x-circle'
                    : 'loader-2'
              }
              size={12}
              className={
                rung.state === 'served'
                  ? 'text-emerald-400'
                  : rung.state === 'failed'
                    ? 'text-slate-600'
                    : 'animate-spin text-cyan-400'
              }
            />
            <span
              dir="ltr"
              className={
                rung.state === 'served'
                  ? 'text-emerald-300'
                  : rung.state === 'failed'
                    ? 'text-slate-600 line-through'
                    : 'text-cyan-200'
              }
            >
              {shortModel(rung.model)}
            </span>
            {rung.tier === 'premium' && <Icon name="crown" size={10} className="text-amber-400" />}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The model's reasoning, streamed.
 *
 * Collapsed by default with a live character counter on the header — which
 * doubles as the progress indicator during the long pause before the first
 * report token, and is the reason that pause does not read as a hang.
 */
function ReasoningDrawer({ text, open, streaming, onToggle, scrollRef }) {
  if (!text) return null;

  return (
    <div className="ai-reasoning-shell overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2 text-right transition-colors hover:bg-white/[0.03]"
      >
        <Icon
          name="brain"
          size={13}
          className={streaming ? 'animate-pulse text-purple-300' : 'text-purple-400/70'}
        />
        <span className="text-[11px] font-bold text-slate-300">
          {streaming ? 'النموذج يفكّر…' : 'مسار تفكير النموذج'}
        </span>
        <span className="text-[10px] text-slate-600">{text.length} حرف</span>
        <span className="flex-1" />
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} className="text-slate-500" />
      </button>

      {open && (
        <div
          ref={scrollRef}
          className="custom-scrollbar ai-reasoning max-h-44 overflow-y-auto border-t border-white/[0.07] px-3 py-2"
        >
          {text}
          {streaming && <span className="ai-caret" />}
        </div>
      )}
    </div>
  );
}

/**
 * Inline markdown, rendered.
 *
 * The prompt's own template uses `**الخلاصة:**`, so a renderer that ignores it
 * shows the asterisks to the doctor. Two forms only — bold and code — because
 * that is all a ten-line report ever contains.
 */
function Inline({ segments, text }) {
  const parts = segments || (text ? [{ text, bold: false, code: false }] : []);

  return (
    <>
      {parts.map((segment, index) => {
        if (segment.bold) {
          return (
            <strong key={index} className="font-black">
              {segment.text}
            </strong>
          );
        }
        if (segment.code) {
          return (
            <code
              key={index}
              className="rounded border border-white/10 bg-white/10 px-1 py-px font-mono text-[0.9em]"
            >
              {segment.text}
            </code>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </>
  );
}

/** The report itself. */
function Findings({ analysis }) {
  const { findings, isStreaming, status, overBudget, lineBudget, followedFormat } = analysis;

  if (findings.length === 0) {
    if (isStreaming) {
      return (
        <div className="space-y-2 py-2">
          {[0, 1, 2].map((row) => (
            <div
              key={row}
              className="h-11 animate-pulse rounded-xl bg-white/[0.04]"
              style={{ animationDelay: `${row * 140}ms` }}
            />
          ))}
        </div>
      );
    }

    // The model answered but ignored the report format entirely — the failure
    // mode a reasoning model produces when it narrates instead of reporting.
    // Saying so and offering a retry is honest; rendering its monologue as
    // clinical findings, which is what used to happen, is not.
    if (status === 'done' && !followedFormat) {
      return (
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.07] p-4">
          <div className="mb-1.5 flex items-center gap-2 text-sm font-bold text-amber-200">
            <Icon name="alert-triangle" size={15} />
            لم يلتزم النموذج بصيغة التقرير
          </div>
          <p className="text-xs leading-relaxed text-slate-400">
            أعاد النموذج نصاً حراً بدل الملاحظات المرقّمة، فلم يُعرض شيء منه هنا تجنباً لعرض
            مسار تفكيره كنتائج طبية. أعد التحليل — غالباً يستجيب نموذج آخر في السلسلة بالصيغة
            الصحيحة.
          </p>
        </div>
      );
    }

    if (status === 'done') {
      return (
        <p className="py-8 text-center text-sm text-slate-500">
          لم يُرجع النموذج أي ملاحظات. جرّب إعادة التحليل.
        </p>
      );
    }
    return null;
  }

  return (
    <>
      <ul className="space-y-2">
        {findings.map((finding, index) => (
          <li
            key={finding.id}
            className="ai-finding p-3"
            data-tone={finding.tone}
            // Staggered, but only for the first ten — a burst that arrives at
            // once should still cascade, while a long tail should not make the
            // doctor wait seconds for the last line to fade in.
            style={{ animationDelay: `${Math.min(index, 9) * 55}ms` }}
          >
            <div className="flex items-start gap-2.5">
              <Icon
                name={TONE_ICON[finding.tone] || 'circle'}
                size={15}
                className={`mt-0.5 shrink-0 ${TONE_TEXT[finding.tone] || 'text-slate-400'}`}
              />

              <div className="min-w-0 flex-1">
                {TONE_LABEL[finding.tone] && (
                  <div
                    className={`mb-0.5 text-[9.5px] font-black ${
                      TONE_TEXT[finding.tone] || 'text-slate-500'
                    } opacity-70`}
                  >
                    {TONE_LABEL[finding.tone]}
                  </div>
                )}

                <p className="text-[13.5px] font-bold leading-relaxed text-white">
                  <Inline segments={finding.claimSegments} text={finding.claim} />
                </p>

                {/* The cause is styled as secondary on purpose. It is the
                    model's inference, and it must not read with the same
                    authority as the observation it was drawn from. */}
                {finding.cause && (
                  <p className="mt-1 text-[12.5px] leading-relaxed text-slate-400">
                    <Inline segments={finding.causeSegments} text={finding.cause} />
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {isStreaming && (
        <div className="px-1 text-slate-500">
          <span className="ai-caret" />
        </div>
      )}

      {/* The ten-line cap is a promise made to the doctor ("read this instead
          of the file"). When the model breaks it, saying so is more honest than
          silently truncating a clinical report. */}
      {!isStreaming && overBudget && (
        <p className="pt-1 text-[10px] text-amber-400/70">
          تجاوز التقرير الحد المقرر ({findings.length} من {lineBudget} أسطر).
        </p>
      )}
    </>
  );
}

function Notice({ tone, icon, text }) {
  const tones = {
    amber: 'border-amber-500/25 bg-amber-500/10 text-amber-200',
    sky: 'border-sky-500/25 bg-sky-500/10 text-sky-200',
    red: 'border-red-500/25 bg-red-500/10 text-red-200',
  };
  return (
    <div className={`flex items-start gap-2 rounded-xl border p-2.5 text-xs ${tones[tone]}`}>
      <Icon name={icon} size={14} className="mt-px shrink-0" />
      <span className="leading-relaxed">{text}</span>
    </div>
  );
}

function ToolButton({ icon, label, onClick, disabled = false, tone = 'default' }) {
  const tones = {
    default:
      'border-white/10 bg-white/5 text-slate-300 hover:border-cyan-400/40 hover:bg-cyan-500/10 hover:text-cyan-200',
    danger:
      'border-red-500/30 bg-red-500/10 text-red-300 hover:border-red-400/60 hover:bg-red-500/20',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-all disabled:cursor-not-allowed disabled:opacity-40 ${tones[tone]}`}
    >
      <Icon name={icon} size={13} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export default MedicalAiAnalyzer;
